import React, { useState } from "react";
import { useNavigate } from "react-router";

import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { BellIcon } from "@heroicons/react/24/solid";

import Button from "~/atoms/buttons/Button";
import ButtonBase from "~/atoms/buttons/ButtonBase";
import useGetPaginatedNotifications from "~/hooks/notifications/useGetPaginatedNotifications";
import useSetNotificationAsRead from "~/hooks/notifications/useSetNotificationAsRead";
import useWindowSize from "~/hooks/useWindowSize";
import { BaseDrawer, DrawerHeader } from "~/molecules/Drawers/Base";
import type { Notification } from "~/types";
import notificationFormatter from "~/util/notificationFormatter";
import { sanitizeNotification } from "~/util/sanitizeHtml";

const BellInner = () => {
  const { totalItems } = useGetPaginatedNotifications({
    markedRead: false,
  });

  const showDot = (totalItems && totalItems > 0) || false;

  return (
    <>
      <span className="sr-only">View notifications</span>
      <BellIcon className="h-6 w-6" aria-hidden="true" />
      <span
        className={`absolute top-2.5 left-2 h-3 w-3 translate-x-1/4 -translate-y-1/4 transform rounded-full border-2 border-white bg-red-500 transition-all duration-300 ease-in-out ${
          showDot ? "scale-100 opacity-100" : "scale-0 opacity-0"
        }`}
      />
    </>
  );
};

type NotificationListProps = {
  notifications: Notification[];
  onActionClick: () => void;
};

const NotificationList = ({
  notifications,
  onActionClick,
}: NotificationListProps) => {
  const navigate = useNavigate();
  const { markAsRead } = useSetNotificationAsRead();

  return (
    <ul className="max-h-screen divide-y divide-gray-100 overflow-y-auto md:max-h-[350px]">
      {notifications.map((notification) => {
        const formattedNotification = notificationFormatter(notification);

        if (!formattedNotification) {
          return null;
        }

        return (
          <li key={notification.id} className="p-4">
            <span className="flex flex-col justify-between md:flex-row">
              <span className="mr-8 flex-1">
                <span className="relative flex items-center gap-3">
                  <span
                    className={`absolute top-2 left-0 inline-block h-1.5 w-1.5 rounded-full bg-${formattedNotification.pill.color}-300 border border-${formattedNotification.pill.color}-700`}
                    aria-hidden="true"
                  />

                  <span
                    className="ml-4 text-sm font-normal text-gray-600"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeNotification(
                        formattedNotification.description
                      ),
                    }}
                  />
                </span>
                <span className="mt-1 ml-4 flex gap-3">
                  <time
                    dateTime={formattedNotification.createdAtIso}
                    className="text-sm text-gray-500"
                  >
                    <span className="sr-only">Notification created at </span>
                    {formattedNotification.createdAtHuman}
                  </time>
                </span>
              </span>

              <span className="mt-2 block w-full md:mt-0 md:w-auto">
                <Button
                  size="sm"
                  variant={formattedNotification.action.variant}
                  color={formattedNotification.action.color}
                  onClick={() => {
                    navigate(formattedNotification.action.href as string);
                    markAsRead(notification.id);
                    onActionClick();
                  }}
                  fullWidth
                >
                  {formattedNotification.action.text}
                </Button>
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
};

const NotificationContent = ({
  data,
  onActionClick,
}: {
  data: Notification[];
  onActionClick: () => void;
}) => {
  if (!data?.length) {
    return (
      <div className="p-4 text-center text-gray-500">
        No unread notifications
      </div>
    );
  }

  return (
    <NotificationList notifications={data} onActionClick={onActionClick} />
  );
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [windowWidth] = useWindowSize();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { data } = useGetPaginatedNotifications({
    markedRead: false,
  });

  const isMobile = windowWidth && windowWidth < 768;

  const initialFocusRef = React.useRef(
    null
  ) as unknown as React.RefObject<HTMLElement>;

  if (isMobile) {
    const onClose = () => setIsDrawerOpen(false);

    return (
      <>
        <ButtonBase
          onClick={() => setIsDrawerOpen(true)}
          className="relative -m-2.5 p-2.5 text-gray-400 hover:text-gray-500 focus:outline-hidden"
        >
          <BellInner />
        </ButtonBase>

        <BaseDrawer
          open={isDrawerOpen}
          onClose={onClose}
          initialFocus={initialFocusRef}
        >
          <DrawerHeader onClose={onClose} title="Unread Notifications">
            <NotificationContent data={data} onActionClick={close} />
          </DrawerHeader>
          <div className="flex gap-4 px-4 py-6 sm:px-6">
            <div className="flex-1">
              <Button
                variant="outlined"
                color="tertiary"
                size="sm"
                fullWidth
                onClick={() => {
                  navigate("/notifications");
                  close();
                }}
              >
                View All Notifications
              </Button>
            </div>
          </div>
        </BaseDrawer>
      </>
    );
  }

  return (
    <Popover className="relative h-6" as="div">
      {({ open, close }) => (
        <>
          <PopoverButton className="relative -m-2.5 p-2.5 text-gray-400 hover:text-gray-500 focus:outline-hidden">
            <BellInner />
          </PopoverButton>

          <Transition
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <PopoverPanel className="absolute right-0 mt-3 max-h-[500px] w-[500px] overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden">
              <div className="divide-y divide-gray-100">
                <div className="p-4">
                  <h2 className="text-lg font-bold text-gray-900">
                    Unread Notifications
                  </h2>
                </div>

                <NotificationContent data={data} onActionClick={close} />

                <div className="p-4">
                  <Button
                    variant="outlined"
                    color="tertiary"
                    size="sm"
                    fullWidth
                    onClick={() => {
                      navigate("/notifications");
                      close();
                    }}
                  >
                    View All Notifications
                  </Button>
                </div>
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default NotificationBell;
